"use client"

// useStandaloneDecompose — fetches a page of standalone line_items for ONE
// row identity (sku | family_key) in the matrix's current period. Backed by
// /api/integrations/shopify/bundle-sku-matrix/standalone-line-items.
//
// Lazy: pass `enabled=false` until the Sheet actually opens. Module-level
// ResponseCache shares the 5-min TTL pattern with use-bundle-performance /
// use-bundle-type-breakdown — re-opening the SAME row within the window
// skips the network round-trip.

import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type {
  MatrixGroupBy,
  StandaloneLineItemsResponse,
} from "@/lib/shopify/bundles/types"
import type {
  StandaloneSortDir,
  StandaloneSortKey,
} from "@/lib/shopify/bundles/standalone-line-items"
import { ResponseCache } from "./_response-cache"

export interface UseStandaloneDecomposeOptions {
  integrationId: string
  periodStart: string
  periodEnd: string
  groupBy: MatrixGroupBy
  /** SKU-mode identity. Ignored in Family mode. */
  sku: string | null
  /** Family-mode identity. Ignored in SKU mode. */
  familyKey: string | null
  minimumOrderTotal?: number
  freeShippingThreshold?: number
  /** 1-based; server clamps to 200. */
  limit?: number
  offset?: number
  /** SKUs the user excluded ("eye" toggle) — server drops them before pagination. */
  excludeSkus?: string[]
  /** Sort column; null → server default (processed desc). */
  sortBy?: StandaloneSortKey | null
  sortDir?: StandaloneSortDir
  /** Skip auto-fetch until true. Drives lazy load on Sheet open. */
  enabled?: boolean
}

export interface UseStandaloneDecomposeResult {
  data: StandaloneLineItemsResponse | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const CACHE_TTL_MS = 5 * 60 * 1000
const cache = new ResponseCache<StandaloneLineItemsResponse>(CACHE_TTL_MS)

function cacheKey(opts: {
  userId: string
  activeTenantId: string
  integrationId: string
  periodStart: string
  periodEnd: string
  groupBy: MatrixGroupBy
  identity: string
  minimumOrderTotal: number
  freeShippingThreshold: number
  limit: number
  offset: number
  excludeKey: string
  sortKey: string
}): string {
  return [
    opts.userId,
    opts.activeTenantId,
    opts.integrationId,
    opts.periodStart,
    opts.periodEnd,
    opts.groupBy,
    opts.identity,
    opts.minimumOrderTotal,
    opts.freeShippingThreshold,
    opts.limit,
    opts.offset,
    opts.excludeKey,
    opts.sortKey,
  ].join("|")
}

export function useStandaloneDecompose({
  integrationId,
  periodStart,
  periodEnd,
  groupBy,
  sku,
  familyKey,
  minimumOrderTotal = 0,
  freeShippingThreshold = 0,
  limit = 50,
  offset = 0,
  excludeSkus = [],
  sortBy = null,
  sortDir = "desc",
  enabled = true,
}: UseStandaloneDecomposeOptions): UseStandaloneDecomposeResult {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const userId = user?.user_id ?? ""
  const activeTenantId = user?.impersonated_tenant_id ?? user?.tenant_id ?? ""

  const [data, setData] = useState<StandaloneLineItemsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const identity = groupBy === "sku" ? sku ?? "" : familyKey ?? ""

  // Stable, order-independent dependency for the exclude set (new array
  // identity each render would otherwise re-fire the fetch).
  const excludeKey = JSON.stringify([...excludeSkus].sort())
  const sortKey = sortBy ? `${sortBy}:${sortDir}` : ""

  const requestSeqRef = useRef(0)

  useEffect(() => {
    if (!isAuthenticated) cache.clear()
  }, [isAuthenticated])

  const fetchData = useCallback(
    async (useCache: boolean) => {
      const localSeq = ++requestSeqRef.current

      if (
        !integrationId ||
        !isAuthenticated ||
        !periodStart ||
        !periodEnd ||
        !userId ||
        !activeTenantId ||
        identity.length === 0
      ) {
        if (localSeq === requestSeqRef.current) {
          setData(null)
          setError(null)
          setIsLoading(false)
        }
        return
      }

      const key = cacheKey({
        userId,
        activeTenantId,
        integrationId,
        periodStart,
        periodEnd,
        groupBy,
        identity,
        minimumOrderTotal,
        freeShippingThreshold,
        limit,
        offset,
        excludeKey,
        sortKey,
      })

      if (useCache) {
        const cached = cache.get(key)
        if (cached != null) {
          if (localSeq === requestSeqRef.current) {
            setData(cached)
            setError(null)
            setIsLoading(false)
          }
          return
        }
      }

      setIsLoading(true)
      setError(null)

      try {
        const params = new URLSearchParams({
          integration_id: integrationId,
          period_start: periodStart,
          period_end: periodEnd,
          group_by: groupBy,
          minimum_order_total: String(minimumOrderTotal),
          free_shipping_threshold: String(freeShippingThreshold),
          limit: String(limit),
          offset: String(offset),
        })
        if (groupBy === "sku") params.set("sku", identity)
        else params.set("family_key", identity)
        // Reconstruct from excludeKey (stable dep) so the array's per-render
        // identity doesn't churn the useCallback.
        for (const ex of JSON.parse(excludeKey) as string[]) params.append("exclude_sku", ex)
        if (sortBy) {
          params.set("sort_by", sortBy)
          params.set("sort_dir", sortDir)
        }

        const res = await fetchWithAuth(
          `/api/integrations/shopify/bundle-sku-matrix/standalone-line-items?${params}`,
        )
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        const payload: StandaloneLineItemsResponse = await res.json()
        cache.set(key, payload)
        if (localSeq === requestSeqRef.current) {
          setData(payload)
        }
      } catch (err: unknown) {
        if (localSeq === requestSeqRef.current) {
          const message = err instanceof Error ? err.message : "Failed to load standalone line items"
          setError(message)
          setData(null)
        }
      } finally {
        if (localSeq === requestSeqRef.current) setIsLoading(false)
      }
    },
    [
      integrationId,
      isAuthenticated,
      userId,
      activeTenantId,
      periodStart,
      periodEnd,
      groupBy,
      identity,
      minimumOrderTotal,
      freeShippingThreshold,
      limit,
      offset,
      excludeKey,
      sortBy,
      sortDir,
      sortKey,
      fetchWithAuth,
    ],
  )

  const refresh = useCallback(async () => {
    if (integrationId && periodStart && periodEnd && userId && activeTenantId && identity.length > 0) {
      cache.delete(
        cacheKey({
          userId,
          activeTenantId,
          integrationId,
          periodStart,
          periodEnd,
          groupBy,
          identity,
          minimumOrderTotal,
          freeShippingThreshold,
          limit,
          offset,
          excludeKey,
          sortKey,
        }),
      )
    }
    await fetchData(false)
  }, [
    fetchData,
    userId,
    activeTenantId,
    integrationId,
    periodStart,
    periodEnd,
    groupBy,
    identity,
    minimumOrderTotal,
    freeShippingThreshold,
    limit,
    offset,
    excludeKey,
    sortKey,
  ])

  useEffect(() => {
    if (!enabled) return
    void fetchData(true)
  }, [enabled, fetchData])

  return { data, isLoading, error, refresh }
}
